"use client";

import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { Linkedin } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useConfig } from "@/components/providers/config";
import { normalizeUrl } from "@/lib/utils";
import { AnimatedWave } from "../AnimatedWave";
import { NavigationLinks } from "./navigation-links";

export function Footer({ className }: { className?: string }) {
  const { config } = useConfig();
  const currentYear = new Date().getFullYear();

  // Extract call to action link from config
  // Check for 'link' first (new Config type), then fallback to 'call_to_action_link' for backwards compatibility
  const ctaLink =
    (config as Record<string, unknown>)?.link ??
    (config as Record<string, unknown>)?.call_to_action_link ??
    null;
  const ctaHref: string =
    (ctaLink as { cached_url?: string; url?: string })?.cached_url ??
    (ctaLink as { cached_url?: string; url?: string })?.url ??
    "/contact";
  const ctaLabel: string =
    (ctaLink as { title?: string })?.title ??
    (typeof (config as Record<string, unknown>)?.call_to_action_label ===
    "string"
      ? ((config as Record<string, unknown>).call_to_action_label as string)
      : undefined) ??
    "Schedule A Meeting";

  const handleFooterCtaClick = () => {
    posthog.capture("footer_cta_clicked", {
      cta_label: ctaLabel,
      cta_href: normalizeUrl(ctaHref),
      cta_location: "footer",
    });
  };

  return (
    <div className="relative mt-24">
      <AnimatedWave
        backgroundColor="purple"
        strokeColor="teal"
        position="top"
      />
      <footer className={cn("bg-violet-950", className)}>
        <div className="container mx-auto px-4 sm:px-6 pt-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-center">
            {/* Logo and Description */}
            <div className="space-y-4 text-center md:text-left">
              <div className="shrink-0">
                <Image
                  src="/images/logo.png"
                  alt="The Learning Lab"
                  width={200}
                  height={200}
                  className="h-18 w-auto mx-auto md:mx-0"
                />
              </div>
              <p className="text-violet-300 font-medium drop-shadow-2xl text-center md:text-left">
                {typeof (config as Record<string, unknown>)
                  ?.site_description === "string"
                  ? ((config as Record<string, unknown>)
                      .site_description as string)
                  : "Empowering teams through innovative learning solutions."}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Button size="lg" asChild className="mx-auto md:mx-0">
                  <Link
                    href={normalizeUrl(ctaHref)}
                    onClick={handleFooterCtaClick}
                  >
                    {ctaLabel}
                  </Link>
                </Button>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" asChild>
                    <Link
                      href="https://medium.com/@scott_43276"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        fill="#ffffff"
                        role="img"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        className="size-6"
                      >
                        <title>Medium</title>
                        <path d="M4.21 0A4.201 4.201 0 0 0 0 4.21v15.58A4.201 4.201 0 0 0 4.21 24h15.58A4.201 4.201 0 0 0 24 19.79v-1.093c-.137.013-.278.02-.422.02-2.577 0-4.027-2.146-4.09-4.832a7.592 7.592 0 0 1 .022-.708c.093-1.186.475-2.241 1.105-3.022a3.885 3.885 0 0 1 1.395-1.1c.468-.237 1.127-.367 1.664-.367h.023c.101 0 .202.004.303.01V4.211A4.201 4.201 0 0 0 19.79 0Zm.198 5.583h4.165l3.588 8.435 3.59-8.435h3.864v.146l-.019.004c-.705.16-1.063.397-1.063 1.254h-.003l.003 10.274c.06.676.424.885 1.063 1.03l.02.004v.145h-4.923v-.145l.019-.005c.639-.144.994-.353 1.054-1.03V7.267l-4.745 11.15h-.261L6.15 7.569v9.445c0 .857.358 1.094 1.063 1.253l.02.004v.147H4.405v-.147l.019-.004c.705-.16 1.065-.397 1.065-1.253V6.987c0-.857-.358-1.094-1.064-1.254l-.018-.004zm19.25 3.668c-1.086.023-1.733 1.323-1.813 3.124H24V9.298a1.378 1.378 0 0 0-.342-.047Zm-1.862 3.632c-.1 1.756.86 3.239 2.204 3.634v-3.634z" />
                      </svg>
                    </Link>
                  </Button>
                  <Button variant="ghost" size="icon" asChild>
                    <Link
                      href="https://www.linkedin.com/in/scottleiper/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="size-6 text-white" />
                    </Link>
                  </Button>
                </div>
              </div>
              <Image
                src="/images/hr-award.png"
                alt="Most Influencial Thinker"
                height={1080}
                width={1080}
                className="h-16 w-auto rounded-md shadow-xl"
              />
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-col gap-2">
              <NavigationLinks navigationType="footer" />
            </nav>
          </div>

          {/* Bottom Bar */}
          <div className="flex pt-4 flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-violet-50 font-medium">
              © {currentYear} The Learning Lab. All rights reserved.
              <br />
              Website by{" "}
              <Link
                href="https://studiolat53.com"
                target="_blank"
                className="text-teal-500 hover:text-teal-600 transition-colors font-semibold"
              >
                LATITUDE53
              </Link>
            </p>
            <div className="flex gap-4">
              <Link
                href="/privacy"
                className="text-sm text-violet-50 hover:text-teal-500 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm text-violet-50 hover:text-teal-500 transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
